import { RouteEnum } from '@/constants/enums/RouteEnum';
import { UserPlanTypeEnum } from '@/constants/user';
import auth from '@/middleware/auth';
import lineBinding from '@/middleware/lineBinding';
import urlToken from '@/middleware/urlToken';
import webview from '@/middleware/webview';
import type { RouteMenuItem } from '@/models/interface/routeMeta';
import type { RouteRecordRaw } from 'vue-router';

/**
 * 基礎可用功能路由
 */
export const baseFeatureRoutes: RouteRecordRaw[] = [
  {
    /**
     * 聽力訓練
     */
    path: '/listening',
    name: RouteEnum.Listening,
    component: () => import('@/views/ListeningView.vue'),
    meta: {
      menu: { icon: 'headphone', label: '聽力訓練' } as RouteMenuItem,
      middleware: [auth]
    }
  },
  {
    /**
     * 聽力訓練 - 克漏字
     */
    path: '/listening-test',
    name: RouteEnum.ListeningTest,
    component: () => import('@/views/ListeningTestView.vue'),
    meta: {
      middleware: [auth]
    }
  },
  {
    /**
     * 精讀課程
     */
    path: '/reading',
    name: RouteEnum.Reading,
    component: () => import('@/views/reading/index.vue'),
    meta: {
      menu: { icon: 'reading', label: '精讀課程' } as RouteMenuItem,
      middleware: [auth]
    }
  },
  {
    /**
     * 文章詳讀頁
     */
    path: '/article/:id',
    name: RouteEnum.Article,
    component: () => import('@/views/ArticleView.vue'),
    meta: {
      middleware: [auth]
    }
  },
  {
    /**
     * 複製文章頁
     */
    path: '/articleCopy',
    name: RouteEnum.ArticleCopy,
    component: () => import('@/views/ArticleCopyView.vue'),
    meta: {
      middleware: [auth]
    }
  },
  {
    /**
     * 會話練習
     */
    path: '/speaking',
    name: RouteEnum.Speaking,
    component: () => import('@/views/SpeakingView.vue'),
    meta: {
      menu: { icon: 'messages2', label: '會話練習' } as RouteMenuItem,
      middleware: [auth]
    }
  },
  {
    /**
     * 書寫講堂
     */
    path: '/writing',
    name: RouteEnum.Writing,
    component: () => import('@/views/WritingView.vue'),
    meta: {
      menu: { icon: 'editPen', label: '書寫講堂' } as RouteMenuItem,
      middleware: [auth]
    }
  },
  {
    /**
     * 大課表
     */
    path: '/schedule',
    name: RouteEnum.Schedule,
    component: () => import('@/views/ScheduleView.vue'),
    meta: {
      menu: { icon: 'calendar', label: '課表' } as RouteMenuItem,
      middleware: [lineBinding, auth]
    }
  },
  {
    /**
     * 大課表 - 進階搜尋
     */
    path: '/advanced-search',
    name: RouteEnum.AdvancedSearch,
    component: () => import('@/views/AdvancedSearchView.vue'),
    meta: {
      middleware: [auth]
    }
  },
  {
    /**
     * 課程詳細頁
     */
    path: '/lesson',
    name: RouteEnum.Lesson,
    component: () => import('@/views/lesson/index.vue'),
    meta: {
      middleware: [auth]
    }
  },
  {
    /**
     * 複習紀錄
     */
    path: '/review',
    name: RouteEnum.Review,
    component: () => import('@/views/ReviewView.vue'),
    meta: {
      menu: { icon: 'review', label: '複習紀錄' } as RouteMenuItem,
      middleware: [auth]
    }
  },
  {
    /**
     * 我的收錄
     */
    path: '/collection',
    name: RouteEnum.Collection,
    component: () => import('@/views/CollectionView.vue'),
    meta: {
      menu: { icon: 'archiveTick', label: '我的收錄' } as RouteMenuItem,
      middleware: [auth]
    }
  },
  {
    /**
     * 每週測驗
     */
    path: '/weeklyTest',
    name: RouteEnum.WeeklyTest,
    component: () => import('@/views/WeeklyTest.vue'),
    meta: {
      middleware: [auth]
    }
  },
  {
    /**
     * 每週測驗紀錄
     */
    path: '/weeklyTestRecord',
    name: RouteEnum.WeeklyTestRecord,
    component: () => import('@/views/WeeklyTestRecord.vue'),
    meta: {
      middleware: [auth]
    }
  },
  {
    /**
     * 直播中課程列表 (直播室過渡頁)
     * @remarks
     * - v1、v2 會員共用同一頁 (allowAllVersions)，故不套 Layout，
     *   由 `LiveNowView` 依會員版本渲染對應頁首/頁尾
     * - 路徑以 `/live` 開頭，B2B 會員亦可造訪
     */
    path: '/live-now',
    name: RouteEnum.LiveNow,
    component: () => import('@/views/LiveNowView.vue'),
    meta: {
      layout: 'NoLayout',
      allowAllVersions: true,
      middleware: [urlToken, webview, auth]
    }
  },
  {
    /**
     * 直播 - 入口頁
     */
    path: '/live',
    name: RouteEnum.Live,
    component: () => import('@/views/LiveView.vue'),
    meta: {
      middleware: [webview, auth]
    }
  },
  {
    /**
     * 直播 - 直播間頁
     */
    path: '/live/:channelName',
    name: RouteEnum.LiveRoom,
    component: () => import('@/views/LiveRoomView.vue'),
    meta: {
      layout: 'NoLayout',
      allowAllVersions: true,
      middleware: [urlToken, webview, auth]
    }
  },
  {
    /**
     * 學員心得分享編輯頁
     * @remarks 需登入驗證
     */
    path: '/student-testimonials-sharing',
    name: RouteEnum.StudentTestimonialsSharing,
    component: () => import('@/views/testimonials/TestimonialsSharingView.vue'),
    meta: {
      layout: 'NoLayout',
      allowAllVersions: true,
      middleware: [auth]
    }
  },
  {
    /**
     * 點數使用紀錄
     */
    path: '/usageRecord',
    name: RouteEnum.UsageRecord,
    component: () => import('@/views/UsageRecord.vue'),
    meta: {
      middleware: [auth]
    },
    beforeEnter: async () => {
      /**
       * 2026/04/01 起，定期制會員無法進入點數使用紀錄頁
       */
      const userStore = useUserStore();
      if (userStore.userData.planType === UserPlanTypeEnum.Ayce) {
        return { name: RouteEnum.Index };
      }
    }
  },
  // 2026/07/20 移除 v1 舊版訂閱頁
  // {
  //   /**
  //    * 訂閱付款頁
  //    */
  //   path: '/newSubscription/orderPlan',
  //   name: RouteEnum.OrderPlan,
  //   component: () => import('@/views/subscription/OrderPlanView.vue'),
  //   meta: {
  //     layout: 'NoLayout',
  //     middleware: [webview, auth]
  //   }
  // },
  // {
  //   /**
  //    * 訂閱感謝頁
  //    */
  //   path: '/newSubscription/paymentResult',
  //   name: RouteEnum.PaymentResult,
  //   component: () => import('@/views/subscription/paymentResult.vue'),
  //   meta: {
  //     layout: 'NoLayout',
  //     middleware: [auth]
  //   }
  // },
  {
    /**
     * 捐贈頁
     */
    path: '/donation/:orderNumber',
    name: RouteEnum.Donation,
    component: () => import('@/views/DonationView.vue'),
    meta: {
      layout: 'NoLayout'
    }
  },
  // 2026/07/20 移除 v1 舊版訂閱頁
  // {
  //   /**
  //    * 訂閱頁
  //    */
  //   path: '/newSubscription',
  //   name: RouteEnum.Subscription,
  //   component: () => import('@/views/subscription/index.vue'),
  //   meta: {
  //     layout: 'NoLayout',
  //     middleware: [webview]
  //   },
  //   beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  //     if (from.name && !to.query.Show) {
  //       return { name: RouteEnum.Subscription, query: { Show: '1' } };
  //     }
  //   }
  // },
  // {
  //   /**
  //    * 特殊方案訂閱付款頁
  //    */
  //   path: '/newSubscription/special/orderPlan',
  //   name: RouteEnum.SpecialOrderPlan,
  //   component: () => import('@/views/subscription/specialPlan.vue'),
  //   meta: {
  //     layout: 'NoLayout',
  //     middleware: [auth]
  //   }
  // },
  {
    /**
     * 專屬推薦 (分潤頁)
     */
    path: '/MLM/:groupId',
    name: RouteEnum.MLM,
    component: () => import('@/views/MLM.vue'),
    meta: {
      layout: 'NoLayout',
      middleware: [auth]
    }
  },
  // 2026/07/20 移除 v1 舊版訂閱頁
  // {
  //   /**
  //    * 大單付款頁
  //    */
  //   path: '/bigOrder',
  //   name: RouteEnum.BigOrder,
  //   component: () => import('@/views/BigOrderView.vue'),
  //   meta: {
  //     layout: 'NoLayout',
  //     middleware: [webview, auth]
  //   }
  // },
  // {
  //   /**
  //    * 大單紀錄頁
  //    */
  //   path: '/orderRecord',
  //   name: RouteEnum.OrderRecord,
  //   component: () => import('@/views/OrderRecordView.vue'),
  //   meta: {
  //     middleware: [webview, auth]
  //   }
  // },
  {
    /**
     * 網站地圖頁
     */
    path: '/sitemap',
    name: RouteEnum.Sitemap,
    component: () => import('@/views/sitemap/index.vue'),
    meta: {
      layout: 'NoLayout'
    }
  },
  /**
   * 刪除帳號頁 (原定義給 Android WebView 使用)
   */
  {
    path: '/delete-account',
    name: RouteEnum.DeleteAccount,
    component: () => import('@/views/DeleteAccountView.vue'),
    meta: {
      layout: 'NoLayout',
      allowAllVersions: true,
      middleware: [auth]
    }
  }
  /** 合作夥伴頁 (暫時停用) */
  // {
  //   path: '/partners',
  //   name: RouteEnum.PartnersView,
  //   component: () => import('@/views/PartnersView.vue'),
  //   meta: {
  //     layout: 'NoLayout'
  //   }
  // },
];
